import type { ColumnDef } from '@tanstack/react-table';
import { useMemo } from 'react';

import {
	type AutoSizedColumnConfig,
	useAutoSizedColumns,
} from './useAutoSizedColumns';

type HistoryTableColumnValue = boolean | number | string | null | undefined;

interface UseVisibleHistoryTableColumnsProps<T extends object> {
	columns: ColumnDef<T>[];
	data: T[];
	columnSettings: Partial<Record<string, AutoSizedColumnConfig<T>>>;
}

function getColumnId<T extends object>(column: ColumnDef<T>) {
	if (column.id) {
		return column.id;
	}

	return 'accessorKey' in column && typeof column.accessorKey === 'string'
		? column.accessorKey
		: undefined;
}

function getNormalizedValues(
	value: HistoryTableColumnValue | readonly HistoryTableColumnValue[]
) {
	return Array.isArray(value) ? value : [value];
}

function hasVisibleHistoryTableValue(value: HistoryTableColumnValue) {
	return value !== null && value !== undefined && value !== '';
}

export function filterEmptyHistoryColumns<T extends object>({
	columns,
	data,
	columnSettings,
}: UseVisibleHistoryTableColumnsProps<T>) {
	return columns.filter((column) => {
		const columnId = getColumnId(column);

		if (!columnId) {
			return true;
		}

		const config = columnSettings[columnId];

		if (!config) {
			return true;
		}

		return data.some((row) =>
			getNormalizedValues(config.getValues(row)).some(
				hasVisibleHistoryTableValue
			)
		);
	});
}

export function useVisibleHistoryTableColumns<T extends object>({
	columns,
	data,
	columnSettings,
}: UseVisibleHistoryTableColumnsProps<T>) {
	const visibleColumns = useMemo(
		() => filterEmptyHistoryColumns({ columns, data, columnSettings }),
		[columnSettings, columns, data]
	);

	return useAutoSizedColumns({
		columns: visibleColumns,
		data,
		columnSettings,
	});
}
